import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { SectionHeader } from "@/components/common/Widgets";
import { fmtDate } from "@/lib/format";
import { ListChecks, UserCheck, FileText, AlertTriangle, ChevronRight, Search, MapPin, BellRing } from "lucide-react";

function PriorityBadge({ p }) {
  const cls = p === "HIGH" ? "text-destructive border-destructive/40" : p === "MEDIUM" ? "text-warning border-warning/40" : "text-success border-success/40";
  return <Badge variant="outline" className={`${cls} text-[10px]`}>{p}</Badge>;
}

export default function FOQueuePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/applications/fo-queue");
      setRows(r.data);
    } catch (e) {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter((r) =>
    !q ||
    (r.ref_no || "").toLowerCase().includes(q.toLowerCase()) ||
    (r.customer_name || "").toLowerCase().includes(q.toLowerCase()) ||
    (r.loan_type || "").toLowerCase().includes(q.toLowerCase())
  );

  const isFO = user?.role === "FO";
  const canRunReminders = ["HO", "RO", "BM", "SUPER_ADMIN"].includes(user?.role);
  const [reminderBusy, setReminderBusy] = useState(false);

  const runReminders = async () => {
    if (!window.confirm("Send reminder notifications to FOs of all applications idle > 7 days?")) return;
    setReminderBusy(true);
    try {
      const r = await api.post("/notifications/fo-reminders/run?days_idle=7");
      const d = r.data;
      if (d.sent_count) {
        toast.success(`${d.sent_count} reminder(s) dispatched · ${d.skipped_count} skipped`);
      } else {
        toast.info(`No idle applications found (cutoff ${d.cutoff_date}) · ${d.candidates} candidates checked`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send reminders");
    } finally { setReminderBusy(false); }
  };

  // Aggregate stats
  const total = rows.length;
  const fvPending = rows.filter((r) => !r.fv_done).length;
  const docsLow = rows.filter((r) => r.doc_count < 3).length;
  const high = rows.filter((r) => (r.todo || []).some((t) => t.priority === "HIGH")).length;

  return (
    <div className="space-y-5">
      <SectionHeader
        title={isFO ? "My Field Officer Queue" : "Field Officer Queue"}
        subtitle={isFO
          ? "Branch-originated applications assigned to you. Upload remaining docs and submit field verification."
          : "Branch-originated applications awaiting FO action across your scope."}
        action={
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ref/customer..." className="pl-7 h-8 w-56" data-testid="fo-queue-search" />
            </div>
            {canRunReminders && (
              <Button size="sm" variant="outline" onClick={runReminders} disabled={reminderBusy} data-testid="fo-run-reminders">
                <BellRing className="h-3.5 w-3.5 mr-1" /> {reminderBusy ? "Sending…" : "Send Reminders (>7d)"}
              </Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Assigned</div><div className="text-2xl font-semibold heading mt-1">{total}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">FV pending</div><div className="text-2xl font-semibold heading mt-1 text-warning">{fvPending}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Docs &lt; 3</div><div className="text-2xl font-semibold heading mt-1">{docsLow}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">High priority</div><div className="text-2xl font-semibold heading mt-1 text-destructive">{high}</div></Card>
      </div>

      {loading && <Card className="p-8 text-center text-muted-foreground">Loading…</Card>}
      {!loading && filtered.length === 0 && (
        <Card className="p-10 text-center border-dashed">
          <UserCheck className="h-10 w-10 text-muted-foreground/40 mx-auto mb-2" />
          <p className="font-medium heading">Queue is empty</p>
          <p className="text-xs text-muted-foreground mt-1">{isFO ? "No applications assigned to you right now." : "No branch-originated applications need FO action."}</p>
        </Card>
      )}

      <div className="space-y-3">
        {filtered.map((r) => (
          <Card key={r.id} className="p-4 hover:bg-muted/30 transition-colors cursor-pointer border-border" onClick={() => navigate(`/applications/${r.id}`)} data-testid={`fo-queue-row-${r.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-semibold heading text-sm mono">{r.ref_no}</h4>
                  <Badge variant="secondary" className="text-[10px]">{r.loan_type}</Badge>
                  <Badge variant="outline" className="text-[10px]">{r.stage}</Badge>
                  <Badge variant="outline" className="text-[10px] border-accent/40 text-accent">Origin: {r.origin || "BRANCH"}</Badge>
                  {r.assigned_fo_name && !isFO && <Badge variant="outline" className="text-[10px]"><UserCheck className="h-2.5 w-2.5 mr-1 inline" />{r.assigned_fo_name}</Badge>}
                </div>
                <div className="text-sm font-medium mt-1">{r.customer_name}</div>
                <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-1 flex-wrap">
                  <span className="mono">{r.customer_cif}</span>
                  <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{r.branch}</span>
                  <span>Created {fmtDate(r.created_at)}</span>
                  <span className="flex items-center gap-1"><FileText className="h-3 w-3" />{r.doc_count} docs</span>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            </div>

            {r.todo && r.todo.length > 0 && (
              <div className="mt-3 pt-3 border-t border-border space-y-1.5">
                <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                  <ListChecks className="h-3 w-3" /> Pending actions ({r.todo.length})
                </div>
                {r.todo.map((t, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <PriorityBadge p={t.priority} />
                    <span className={t.kind === "FV_DONE" ? "text-muted-foreground" : ""}>{t.msg}</span>
                    {t.priority === "HIGH" && <AlertTriangle className="h-3 w-3 text-destructive" />}
                  </div>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
